import React from "react"
import { BodyText, InlineText, SubTitle } from "../../Typography/Typography"
import SmartLink from "../../layout/SmartLink/SmartLink"

const providerProfileClickEvent = () => ({
  name: "SEO Filtered Directory - Copy link to provider clicked",
  properties: {
    "Care Type": "care_home",
    "GEO Location": "Colchester",
    "Current URL":
      "https://www.caresourcer.com/s/providers/districts/colchester/care-home/",
  },
})

export default {
  summary: (
    <BodyText marginBottom>
      Choosing a care home for a loved one can be difficult, however with 37
      care homes in Colchester, and 13 offering nursing care, it is important
      you make the right decision on which care home is best for your loved
      one’s needs. Here are a few of our top tips to help you narrow your
      options.
    </BodyText>
  ),
  content: [
    <SubTitle marginBottom bold>
      Location is key
    </SubTitle>,
    <BodyText marginBottom>
      It is important to find a care home where the resident will feel
      comfortable, and location plays a major role in this. Try to make sure the
      home not only provides the type of care required, but is ideally located
      close to friends and family so they can easily visit. There are good
      public transport links throughout Colchester making many of the homes easy
      to get to, and a number are within walking distance from the town centre.
      However, if you have access to a car, it might be good to also look
      towards the outskirts of Colchester, where you will be closer to more
      green space. For example{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-2209923573/woodland-view"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Woodland View Care Home</InlineText>
      </SmartLink>{" "}
      is only a 2-minute walk from High Woods Country Park, which is lovely for
      an afternoon stroll with family and friends.
    </BodyText>,
    <SubTitle marginBottom bold>
      Feeling at home
    </SubTitle>,
    <BodyText marginBottom>
      The process of leaving behind your home and entering new surroundings is
      unsettling for some. So finding a care home where your loved one is able
      to feel at home is important. Many care homes in Colchester allow you to
      move in furniture or to add personal touches to the resident’s room,
      however you should also consider what activities and facilities the care
      home can provide that will help them to feel at ease in their new
      surroundings.
    </BodyText>,
    <SubTitle marginBottom bold italic>
      Gardens and outdoor spaces
    </SubTitle>,
    <BodyText marginBottom>
      How important is outdoor space to your loved one? Are they a keen gardener
      or do they love to sit in the sun with a cup of tea? Understanding what is
      important to your loved one will help you choose from over 30 care homes
      in Colchester which have a garden. If they are green fingered and want to
      help potter in the garden then perhaps it is worth checking out{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-115758629/abberton-manor-nursing-home"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Abberton Manor Nursing Home</InlineText>
      </SmartLink>
      , or if just looking for somewhere to relax in the sun look at{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-136413480/hill-house"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Hill House</InlineText>
      </SmartLink>
      , which has some lovely outdoor space and beautiful seating areas.
    </BodyText>,
    <BodyText marginBottom bold italic>
      Pets welcome
    </BodyText>,
    <BodyText marginBottom>
      Does your loved one have a pet they are worried about leaving when moving
      into a care home? We recommend looking into the 20+ pet-friendly care
      homes in Colchester. Many allow small pets like fish or budgies to move in
      with their owner and others, like{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-133357927/tall-trees"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Tall Trees Care Home</InlineText>
      </SmartLink>
      , allow pets to visit frequently.
    </BodyText>,
    <BodyText marginBottom bold italic>
      Activities and days out
    </BodyText>,
    <BodyText marginBottom>
      To help residents feel at home and continue their hobbies, most care homes
      in Colchester offer a wide range of activities and facilities. Many, like
      the{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-244876318/alderwood-care-home"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Alderwood Care Home</InlineText>
      </SmartLink>
      , offer residents a cinema room, on-site hair salon, cafes and a whole
      range of activities. Several even encourage residents to share and teach
      others their talents, whether it be baking, art or dancing.
    </BodyText>,
  ],
}
